import React, { useEffect, useState, useMemo } from 'react';
import FaceCard from './face-card';
import { useGetCurrentPersonaSession, useGetUser } from '../../hooks/api-hooks';
import NeofaceCompare from './neoface-compare';
import { ArrowPathIcon, EyeIcon, ShieldCheckIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { XCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { Link, useNavigate } from 'react-router-dom';
import NeofaceSelectedModal from './neoface-selected-modal';
import { LoadingPreview } from './loading-face-card';

interface CandidateSelectionStepProps {
  onChangePhoto: () => void;
}

const CandidateSelectionStep: React.FC<CandidateSelectionStepProps> = ({
 onChangePhoto,
}) => {
  const [isFirstPersona, setIsFirstPersona] = useState<boolean>(false);
  const [isPersonaCandidateOpen, setPersonaCandidateOpen] = useState(false);
  const [compareCandidateTargetUrl, setCompareCandidateTargetUrl] = useState<string | null>(null);
  const [compareCandidateId, setCompareCandidateId] = useState<string | null>(null);
  const [compareCandidateSwapUrl, setCompareCandidateSwapUrl] = useState<string | null>(null);
  const [compareSimilarity, setCurrentSimilarity] = useState<number | null>(null);
  const [isNeofaceSelectedModalOpen, setNeofaceSelectedModalOpen] = useState(false);

  const navigate = useNavigate();

  const session = useGetCurrentPersonaSession();
  const user = useGetUser();
  const hasNeoface = user.data?.persona.id;
  const totalCandidatesExpected = session.data?.numberOfCandidates || 8;
  const candidates = session.data?.candidates || [];
  const isOriginalDeleted = session.data?.targetFaceDeletedAt ||
    (session.data?.targetFaceDeletesIn && new Date(session.data.targetFaceDeletesIn) < new Date());

  // Add error state detection
  const isProcessingFailed = session.data?.status === 'failed';
  const failureReason = session.data?.failureReason;

  // Check if processing is taking too long (10 minutes)
  const isProcessingTooLong = useMemo(() => {
    if (!session.data?.createdAt || isProcessingFailed) return false;
    const created = new Date(session.data.createdAt);
    const tenMinutesAgo = new Date(Date.now() - 10 * 60 * 1000);
    return created < tenMinutesAgo && session.data.status === 'processing';
  }, [session.data?.createdAt, isProcessingFailed, session.data?.status]);

  useEffect(() => {
    let pollInterval: NodeJS.Timeout | null = null;

    const startPolling = async () => {
      try {
        const updatedSession = await session.refetch();
        if (updatedSession.data?.status !== 'processing' && pollInterval) {
          clearInterval(pollInterval);
        }
      } catch (error) {
        console.error('Error:', error);
      }
    };

    if (session.isSuccess && session.data?.status === 'processing') {
      pollInterval = setInterval(startPolling, 3000);
    }

    if (session.isSuccess && session.data?.status === 'completed' && pollInterval) {
      clearInterval(pollInterval);
    }

    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [session]);

  /**
   * Selects a neoface for viewing purposes
   */
  const previewNeoface = (personaSessionTargetUrl: string, candidateId: string, personaCandidateSwapUrl: string, similarity: number) => {
    setCompareCandidateTargetUrl(personaSessionTargetUrl);
    setPersonaCandidateOpen(true);
    setCompareCandidateId(candidateId);
    setCompareCandidateSwapUrl(personaCandidateSwapUrl);
    setCurrentSimilarity(similarity);
  };

  const handleNavigateToAnonymizeContent = () => {
    navigate('/anonymize-content', {
      state: { fromCreate: { showCreateNeofaceSurvey: isFirstPersona } }
    });
  };

  const handleContinueBrowsing = () => {
    setNeofaceSelectedModalOpen(false);
  };

  const handleAnonymizeContent = () => {
    setNeofaceSelectedModalOpen(false);
    navigate('/anonymize-content', {
      state: { fromCreate: { showCreateNeofaceSurvey: isFirstPersona } }
    });
  };

  // Function to format error reasons with bullet points
  const formatFailureReason = (reason: string | undefined | null) => {
    if (!reason) return [];

    // Split the message into header and bullet points
    const lines = reason.split('\n').map(line => line.trim());
    const formattedLines = [];

    for (const line of lines) {
      if (line.startsWith('•')) {
        // Handle bullet points
        formattedLines.push({
          type: 'bullet',
          content: line.substring(1).trim()
        });
      } else if (line.length > 0) {
        // Handle regular text
        formattedLines.push({
          type: 'text',
          content: line
        });
      }
    }

    return formattedLines;
  };

  // Render error state if processing failed
  const renderErrorState = () => {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 mt-4">
        <div className="text-center space-y-6">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <XCircleIcon aria-hidden="true" className="h-6 w-6 text-red-600" />
          </div>

          <div>
            <h3 className="text-lg font-medium text-red-800">Neoface Creation Failed</h3>
            <p className="mt-2 text-sm text-gray-600">
              We couldn't generate your Neofaces. This could be due to image quality or technical issues.
            </p>
          </div>

          <div className="rounded-md bg-red-50 p-4 max-w-2xl mx-auto text-left">
            <div className="flex">
              <div className="shrink-0">
                <XCircleIcon aria-hidden="true" className="size-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">Error Details</h3>
                <div className="mt-2 text-sm text-red-700">
                  {formatFailureReason(failureReason).map((line, index) => (
                    line.type === 'bullet' ? (
                      <ul key={index} role="list" className="list-disc space-y-1 pl-5">
                        <li>{line.content}</li>
                      </ul>
                    ) : (
                      <p key={index} className="mb-2">{line.content}</p>
                    )
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-center">
            <button
              onClick={onChangePhoto}
              className="inline-flex items-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold shadow-sm ring-1 ring-inset transition duration-200 ease-in-out text-red-700 ring-red-600 hover:bg-red-50"
            >
              <PhotoIcon className="h-4 w-4 mr-2" />
              Try Again with Different Photo
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render taking too long state
  const renderTakingTooLongState = () => {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 mt-4">
        <div className="text-center space-y-6">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
            <ExclamationTriangleIcon aria-hidden="true" className="h-6 w-6 text-yellow-600" />
          </div>

          <div>
            <h3 className="text-lg font-medium text-yellow-800">Processing Is Taking Longer Than Expected</h3>
            <p className="mt-2 text-sm text-gray-600">
              Your Neofaces are taking longer than usual to generate. You can continue waiting or try with a different photo.
            </p>
          </div>

          <div className="flex justify-center space-x-4">
            <button
              onClick={() => session.refetch()}
              className="inline-flex items-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold shadow-sm ring-1 ring-inset transition duration-200 ease-in-out text-indigo-700 ring-indigo-600 hover:bg-indigo-50"
            >
              <ArrowPathIcon className="h-4 w-4 mr-2" />
              Refresh Status
            </button>
            <button
              onClick={onChangePhoto}
              className="inline-flex items-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold shadow-sm ring-1 ring-inset transition duration-200 ease-in-out text-yellow-700 ring-yellow-600 hover:bg-yellow-50"
            >
              <PhotoIcon className="h-4 w-4 mr-2" />
              Try Different Photo
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Render the candidates grid or error/loading states
  const renderCandidatesGrid = () => {
    if (isProcessingFailed) {
      return renderErrorState();
    }

    return (
      <>
        <div className={'mb-10'}>
          {isProcessingTooLong && renderTakingTooLongState()}
        </div>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4'>
          {Array.from({ length: totalCandidatesExpected }).map((_, idx) => {
            const candidate = candidates[idx];

            if (candidate) {
              return (
                <FaceCard
                  key={candidate.id}
                  personaCandidateId={candidate.id}
                  personaCandidateSwapUrl={candidate.swappedFaceUrl}
                  personaSessionTargetUrl={session.data?.targetFaceUrl || ''}
                  similarity={candidate.similarity}
                  allSimilarities={session.data?.candidates?.map((c: any) => c.similarity) || []}
                  previewNeoface={previewNeoface}
                  isFirstPersona={isFirstPersona}
                />
              );
            }

            return (
              <LoadingPreview
                key={`loading-${idx}`}
                targetFaceUrl={session.data?.targetFaceUrl || ''}
                position={idx + 1}
                total={totalCandidatesExpected}
              />
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div>
      {isNeofaceSelectedModalOpen && (
        <NeofaceSelectedModal
          isOpen={isNeofaceSelectedModalOpen}
          onClose={() => setNeofaceSelectedModalOpen(false)}
          onContinueBrowsing={handleContinueBrowsing}
          onAnonymizeContent={handleAnonymizeContent}
        />
      )}

      {isPersonaCandidateOpen && (
        <NeofaceCompare
          isOpen={isPersonaCandidateOpen}
          setOpen={setPersonaCandidateOpen}
          candidateUrl={compareCandidateSwapUrl}
          candidateId={compareCandidateId}
          targetUrl={compareCandidateTargetUrl}
          similarity={compareSimilarity}
          candidates={session.data?.candidates}
          previewNeoface={previewNeoface}
          personaSession={session.data}
          isFirstPersona={isFirstPersona}
          setIsFirstPersona={setIsFirstPersona}
          setNeofaceSelectedModalOpen={setNeofaceSelectedModalOpen}
        />
      )}

      <h2 className='text-xl font-semibold mb-2'>
        Step 2: Choose Your Neoface
      </h2>
      <p className='text-gray-600 mb-4'>
        Pick your favorite look from these candidates. You can preview any before finalizing.
      </p>

      {/* Privacy notice - shows different message based on deletion status */}
      {isOriginalDeleted ? (
        <div className="bg-green-50 rounded-lg border border-green-100 p-3 mb-6">
          <div className="flex items-center">
            <ShieldCheckIcon className="h-5 w-5 text-green-600 flex-shrink-0" />
            <p className="ml-2 text-sm text-gray-700">
              <span className="font-medium">Privacy protected:</span> Your original face has been permanently deleted from our servers.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-indigo-50 rounded-lg border border-indigo-100 p-3 mb-6">
          <div className="flex items-center">
            <ShieldCheckIcon className="h-5 w-5 text-indigo-600 flex-shrink-0" />
            <p className="ml-2 text-sm text-gray-700">
              <span className="font-medium">Privacy protection in progress:</span> Your original face will be automatically deleted after processing.
            </p>
          </div>
        </div>
      )}

      {/* Action links row - don't show when there's an error */}
      {!isProcessingFailed && (
        <div
          className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 border-b border-gray-200 pb-4'>
          <Link
            to='/my-neofaces'
            className='inline-flex items-center justify-center sm:justify-start gap-2 text-sm text-indigo-600 hover:text-indigo-800 font-medium'
          >
            <EyeIcon className='h-4 w-4' />
            View My Neofaces
          </Link>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={handleNavigateToAnonymizeContent}
              disabled={!hasNeoface}
              className={`
                inline-flex items-center justify-center gap-2 px-4 py-2 text-sm
                font-medium rounded-lg transition-colors duration-150
                ${hasNeoface
                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'}
              `}
            >
              <ArrowPathIcon className="h-4 w-4" />
              Anonymize Content
            </button>

            <button
              onClick={onChangePhoto}
              className='inline-flex items-center justify-center gap-2 px-4 py-2 text-sm
                font-medium text-indigo-600 bg-white border border-indigo-600
                rounded-lg hover:bg-indigo-50 transition-colors duration-150'
            >
              <ArrowPathIcon className='h-4 w-4' />
              Try Different Photo
            </button>
          </div>
        </div>
      )}

      {/* Render candidates grid or error states */}
      {renderCandidatesGrid()}
    </div>
  );
};

export default CandidateSelectionStep;